import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import { closedToSelfServeMember } from './AdminRoute';
import { selfServeRedirectFor } from '../utils/selfServe';
import useSelfServe from '../hooks/useSelfServe';

const Loading = () => (
    <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
    }}>
        <CircularProgress />
    </Box>
);

export const ProtectedRoute = ({ children }) => {
    const { currentUser, userRole, loading, initialized } = useAuth();
    const selfServe = useSelfServe();
    const { pathname } = useLocation();

    // Show loading spinner while checking auth state
    if (loading || !initialized) {
        return <Loading />;
    }

    // If not authenticated, redirect to login
    if (!currentUser) {
        return <Navigate to="/login" replace />;
    }

    // A Google session with no profile yet (needsSetup) has no company or role:
    // it can only finish its account.
    if (currentUser.needsSetup) {
        return <Navigate to="/signup/finish" replace />;
    }

    // Bounce accounts deactivated/suspended/soft-deleted mid-session. AuthContext
    // also force-signs-out on session restore; this catches navigation in between.
    if (currentUser.status === 'inactive' || currentUser.status === 'suspended' || currentUser.status === 'deleted' || currentUser.deleted === true) {
        return <Navigate to="/login" replace />;
    }

    // The self-serve overlay, as AdminRoute applies it: a page outside /admin the role
    // reaches only through a surface hidden from self-serve members (QuickShip's
    // /carriers, /reports) sends a member home instead of rendering reads or calls the
    // server refuses them; the legacy /create-shipment wizard opens the rated form.
    if (closedToSelfServeMember(userRole, pathname)) {
        if (!selfServe.known) return <Loading />;
        if (selfServe.viewer) return <Navigate to={selfServeRedirectFor(pathname)} replace />;
    }

    // If authenticated, show protected content
    return children;
};
